import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Form } from "react-bootstrap";
import CommonButton from "../../commonButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchActiveCities } from "../../../redux/slice/locationSlice";
import { fetchProfessions } from "../../../redux/slice/allUsersSlice";
import withModal from "../../withModal";
import { useToast } from "../../../context/TostContext";
import InputField from "../../form/InputField";
import CustomSelect from "../../form/CustomSelect";

const EditProfileModalInner = ({ onClose, onSubmit, user, isLoading, isEdit = true }) => {
  const dispatch = useDispatch();
  const { cities = [] } = useSelector((state) => state.location || {});
  const { professions = [] } = useSelector((state) => state.allUsers || {});
  const toast = useToast();
  const citySearchTimeoutRef = useRef(null);
  const professionSearchTimeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    whatsapp_number: "",
    website: "",
    address: "",
    about: "",
    city_id: "",
    profession_id: "",
    password: "",
    headline: "",
  });

  const [profileFile, setProfileFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const profileInputRef = useRef(null);

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const coverInputRef = useRef(null);

  const cityOptions = useMemo(() => {
    const fetchedOptions = cities.map((c) => ({ value: c.id, label: c.name }));
    const additionalOptions = [];

    // Always include the current user's city in options if it exists and isn't already in fetched results
    const currentUserCity = user?.city || user?.profile?.city;
    if (currentUserCity && currentUserCity.id && !cities.find(c => c.id === currentUserCity.id)) {
      additionalOptions.push({ value: currentUserCity.id, label: currentUserCity.name });
    }

    // Remove duplicates if any (though logic above prevents it)
    const allOptions = [...additionalOptions, ...fetchedOptions];
    const uniqueOptions = Array.from(new Map(allOptions.map(item => [item.value, item])).values());

    return uniqueOptions;
  }, [cities, user]);

  const professionOptions = useMemo(() => {
    const fetchedOptions = professions.map((p) => ({ value: p.id, label: p.name }));
    const additionalOptions = [];

    // Always include the current user's profession in options if it exists and isn't already in fetched results
    const currentUserProfession = user?.profession || user?.profile?.profession;
    if (currentUserProfession && currentUserProfession.id && !professions.find(p => p.id === currentUserProfession.id)) {
      additionalOptions.push({ value: currentUserProfession.id, label: currentUserProfession.name });
    }

    // Remove duplicates if any
    const allOptions = [...additionalOptions, ...fetchedOptions];
    const uniqueOptions = Array.from(new Map(allOptions.map(item => [item.value, item])).values());

    return uniqueOptions;
  }, [professions, user]);

  const handleCitySearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
      citySearchTimeoutRef.current = setTimeout(() => {
        dispatch(fetchActiveCities({ search: inputValue, limit: 20 }));
      }, 400);
    }
  };

  const handleProfessionSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (professionSearchTimeoutRef.current) clearTimeout(professionSearchTimeoutRef.current);
      professionSearchTimeoutRef.current = setTimeout(() => {
        dispatch(fetchProfessions({ search: inputValue, limit: 20 }));
      }, 400);
    }
  };

  useEffect(() => {
    dispatch(fetchActiveCities({ limit: 20 }));
    dispatch(fetchProfessions({ limit: 20 }));
    if (user) {
      setFormData({
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        mobile_number: user?.mobile_number || "",
        whatsapp_number: user?.profile?.whatsapp_number || "",
        website: user?.profile?.website || "",
        address: user?.profile?.address || "",
        about: user?.profile?.about || "",
        city_id: user?.profile?.city_id || user?.city_id || "",
        profession_id: user?.profile?.profession_id || user?.profession_id || "",
        password: "",
        headline: user?.profile?.headline || "",
      });
      setProfileFile(null);
      setProfilePreview(user?.profile?.profile_photo?.url || null);
      setCoverFile(null);
      setCoverPreview(user?.profile?.cover_photo?.url || null);
    } else {
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        mobile_number: "",
        whatsapp_number: "",
        website: "",
        address: "",
        about: "",
        city_id: "",
        profession_id: "",
        password: "",
        headline: "",
      });
      setProfileFile(null);
      setProfilePreview(null);
      setCoverFile(null);
      setCoverPreview(null);
    }

  }, [user, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCityChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      city_id: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleProfessionChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      profession_id: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleProfileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("Profile photo size should be less than 5MB");
        return;
      }
      setProfileFile(selectedFile);
      setProfilePreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleCoverChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("Cover photo size should be less than 5MB");
        return;
      }
      setCoverFile(selectedFile);
      setCoverPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (!formData.first_name.trim()) {
      toast.error("Please enter a first name");
      return;
    }

    if (!formData.last_name.trim()) {
      toast.error("Please enter a last name");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!formData.mobile_number.trim()) {
      toast.error("Please enter a mobile number");
      return;
    }

    if (!/^[6-9]\d{9}$/.test(formData.mobile_number)) {
      toast.error("Please enter a valid mobile number");
      return;
    }

    if (!formData.profession_id) {
      toast.error("Please select a profession");
      return;
    }

    if (!formData.city_id) {
      toast.error("Please select a city");
      return;
    }

    if (!isEdit) {
      if (!formData.password || formData.password.length < 6) {
        toast.error("Password must be at least 6 characters long");
        return;
      }
    }

    if (formData.whatsapp_number && !/^[6-9]\d{9}$/.test(formData.whatsapp_number)) {
      toast.error("Please enter a valid WhatsApp number");
      return;
    }

    const submitData = new FormData();
    submitData.append("first_name", formData.first_name);
    submitData.append("last_name", formData.last_name);
    submitData.append("email", formData.email || "");
    submitData.append("mobile_number", formData.mobile_number || "");

    submitData.append("about", formData.about || "");
    submitData.append("headline", formData.headline || "");

    let websiteUrl = (formData.website || "").trim();
    if (websiteUrl && !/^https?:\/\//i.test(websiteUrl)) {
      websiteUrl = 'https://' + websiteUrl;
    }
    submitData.append("website", websiteUrl);

    submitData.append("whatsapp_number", formData.whatsapp_number || "");
    submitData.append("address", formData.address || "");
    submitData.append("city_id", formData.city_id || "");
    submitData.append("profession_id", formData.profession_id || "");
    if (!isEdit) {
      submitData.append("password", formData.password);
    }

    if (profileFile) {
      submitData.append("profile_photo", profileFile);
    }
    if (coverFile) {
      submitData.append("cover_photo", coverFile);
    }

    onSubmit(submitData);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Modal.Body>
        {/* Images Section */}
        <div className="mb-4 position-relative px-3 pt-3">
          {/* Cover Photo */}
          <div
            className="position-relative rounded overflow-hidden cursor-pointer w-100"
            style={{
              height: "160px",
              backgroundColor: "#f8f9fa",
              border: coverPreview ? "none" : "2px dashed #dee2e6",
              transition: "all 0.2s"
            }}
            onClick={() => coverInputRef.current?.click()}
            title="Change Cover Photo"
          >
            {coverPreview ? (
              <>
                <img src={coverPreview} alt="Cover Preview" className="w-100 h-100 object-fit-cover" />
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center hover-overlay" style={{ backgroundColor: "rgba(0,0,0,0.3)", opacity: 0, transition: "opacity 0.2s" }} onMouseEnter={(e) => e.currentTarget.style.opacity = 1} onMouseLeave={(e) => e.currentTarget.style.opacity = 0}>
                  <div className="bg-white rounded-circle p-2 shadow-sm d-flex align-items-center justify-content-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                  </div>
                </div>
              </>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted hover-overlay" style={{ transition: "color 0.2s" }} onMouseEnter={(e) => e.currentTarget.style.color = "#495057"} onMouseLeave={(e) => e.currentTarget.style.color = "#6c757d"}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                <span className="small fw-medium">Upload Cover Photo</span>
              </div>
            )}
          </div>
          <input type="file" ref={coverInputRef} onChange={handleCoverChange} accept="image/*" className="d-none" />

          {/* Profile Photo */}
          <div
            className="position-absolute cursor-pointer shadow-sm"
            style={{
              width: "110px",
              height: "110px",
              borderRadius: "50%",
              border: "4px solid #fff",
              backgroundColor: "#fff",
              bottom: "-30px",
              left: "40px",
              zIndex: 10
            }}
            onClick={() => profileInputRef.current?.click()}
            title="Change Profile Photo"
          >
            <div className="w-100 h-100 rounded-circle overflow-hidden bg-light d-flex align-items-center justify-content-center position-relative">
              {profilePreview ? (
                <>
                  <img src={profilePreview} alt="Profile Preview" className="w-100 h-100 object-fit-cover" />
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center hover-overlay" style={{ backgroundColor: "rgba(0,0,0,0.4)", opacity: 0, transition: "opacity 0.2s" }} onMouseEnter={(e) => e.currentTarget.style.opacity = 1} onMouseLeave={(e) => e.currentTarget.style.opacity = 0}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                  </div>
                </>
              ) : (
                <div className="d-flex flex-column align-items-center text-muted hover-overlay" style={{ transition: "color 0.2s" }} onMouseEnter={(e) => e.currentTarget.style.color = "#495057"} onMouseLeave={(e) => e.currentTarget.style.color = "#6c757d"}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                  <span style={{ fontSize: "11px", fontWeight: "500" }}>Upload</span>
                </div>
              )}
            </div>
            <div
              className="position-absolute rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center"
              style={{ width: "32px", height: "32px", bottom: "-2px", right: "-2px", border: "1px solid #eee" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
            </div>
          </div>
          <input type="file" ref={profileInputRef} onChange={handleProfileChange} accept="image/*" className="d-none" />
        </div>

        <div className="grid row-gap-3 px-3 mt-4 pt-3">
          <InputField
            className="g-col-6"
            label={{ name: "First Name" }}
            required
            input={{
              type: "text",
              name: "first_name",
              value: formData.first_name,
              onChange: handleChange,
              placeholder: "Ex: Meet",
            }}
          />
          <InputField
            className="g-col-6"
            label={{ name: "Last Name" }}
            required
            input={{
              type: "text",
              name: "last_name",
              value: formData.last_name,
              onChange: handleChange,
              placeholder: "Ex: Sardhara",
            }}
          />

          <InputField
            className="g-col-6"
            label={{ name: "Headline" }}
            input={{
              type: "text",
              name: "headline",
              value: formData.headline,
              onChange: handleChange,
              placeholder: "Ex: Full Stack Developer / Business Owner",
            }}
          />

          <InputField
            className="g-col-6"
            label={{ name: "Email Address" }}
            required
            input={{
              type: "email",
              name: "email",
              value: formData.email,
              onChange: handleChange,
              placeholder: "Ex: meet@example.com",
            }}
          />

          <InputField
            className="g-col-6"
            label={{ name: "Mobile Number" }}
            required
            input={{
              type: "text",
              name: "mobile_number",
              value: formData.mobile_number,
              onChange: handleChange,
              placeholder: "Ex: 9876543210",
            }}
          />

          {!isEdit && (
            <InputField
              className="g-col-6"
              label={{ name: "Password" }}
              required
              input={{
                type: "password",
                name: "password",
                value: formData.password,
                onChange: handleChange,
                placeholder: "Minimum 6 characters",
              }}
            />
          )}

          <InputField
            className="g-col-6"
            label={{ name: "WhatsApp Number" }}
            input={{
              type: "text",
              name: "whatsapp_number",
              value: formData.whatsapp_number,
              onChange: handleChange,
              placeholder: "Ex: 9876543210",
            }}
          />

          <InputField
            className="g-col-6"
            label={{ name: "Website" }}
            input={{
              type: "text",
              name: "website",
              value: formData.website,
              onChange: handleChange,
              placeholder: "Ex: https://example.com",
            }}
          />

        
            <CustomSelect
             className="g-col-6"
             label={{ name: "Profession" }}
             required
              options={professionOptions}
              value={professionOptions.find(opt => opt.value === formData.profession_id)}
              onChange={handleProfessionChange}
              onInputChange={handleProfessionSearch}
              onMenuOpen={() => {
                dispatch(fetchProfessions({ limit: 20 }));
              }}
              placeholder="Search Profession"
              isClearable
            />
        

          
            <CustomSelect
            className="g-col-6"
              options={cityOptions}
              label={{name:"City"}}
              required
              value={cityOptions.find(opt => opt.value === formData.city_id)}
              onChange={handleCityChange}
              onInputChange={handleCitySearch}
              onMenuOpen={() => {
                dispatch(fetchActiveCities({ limit: 20 }));
              }}
              placeholder="Search City"
              isClearable
             
            />

          <InputField
            className="g-col-6"
            label={{ name: "Address" }}
            input={{
              type: "text",
              name: "address",
              value: formData.address,
              onChange: handleChange,
              placeholder: "Ex: Surat, Gujarat",
            }}
          />

          <InputField
            className="g-col-12"
            label={{ name: "About" }}
            input={{
              as: "textarea",
              rows: 4,
              name: "about",
              value: formData.about,
              onChange: handleChange,
              placeholder: "Write something about yourself",
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <CommonButton
          type="button"
          variant="outline"
          title="Cancel"
          onClick={onClose}
          disabled={isLoading}
        />
        <CommonButton
          type="submit"
          variant="primary"
          title={isEdit ? "Save Changes" : "Create User"}
          isLoading={isLoading}
        />
      </Modal.Footer>
    </Form>
  );
};
const EditProfileModal = withModal(EditProfileModalInner);
export default EditProfileModal
